import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { TrendUp, ChartBar, Users, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { api } from "../../lib/api";

const COLORS = {
  total: "#ffffff",
  pending: "#FFD60A",
  approved: "#00E5FF",
  rejected: "#FF3D71",
  navy: "#04081A",
  surface: "#0D1530",
  line: "#1F2D55",
};

const PIE_PALETTE = ["#00E5FF", "#FFD60A", "#7C3AED", "#FF3D71", "#22D3EE", "#A78BFA", "#F97316", "#10B981", "#EF4444", "#3B82F6", "#EC4899", "#84CC16"];

const RANGES = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
  { label: "365 days", value: 365 },
];

const fmtDate = (d) => {
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return d;
  }
};

const StatCell = ({ label, value, sub, accent }) => (
  <div className="hard-border bg-cyber-surface p-5">
    <div className="term-label flex items-center gap-2">{label}</div>
    <div className={`font-display font-black text-4xl mt-2 tracking-tighter ${accent || "text-white"}`}>{value}</div>
    {sub && <div className="font-mono-tech text-[11px] text-white/50 mt-1">{sub}</div>}
  </div>
);

const ClassCard = ({ label, value, total, accent, dim, sub }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="hard-border bg-cyber-elevated p-5">
      <div className="font-mono-tech text-[10px] text-white/60 uppercase tracking-[0.2em]">/ {label}</div>
      <div className="flex items-baseline gap-3 mt-2">
        <div className={`font-display font-black text-5xl tracking-tighter ${accent}`}>{value}</div>
        <div className="font-mono-tech text-sm text-white/50">{pct}%</div>
      </div>
      <div className="font-mono-tech text-[11px] text-white/50 mt-1">{sub}</div>
      <div className="mt-4 h-1.5 bg-cyber-line relative overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${dim}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="hard-border bg-cyber-black/95 backdrop-blur-sm p-3 text-xs font-mono-tech">
      <div className="text-white/60 uppercase tracking-[0.15em] mb-2">{fmtDate(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-2 text-white/80">
            <span className="w-2 h-2" style={{ background: p.color }} />
            {p.name}
          </span>
          <span className="text-white">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const AdminAnalytics = () => {
  const [days, setDays] = useState(30);
  const [eventId, setEventId] = useState("");
  const [events, setEvents] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/events").then(({ data }) => setEvents(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { days };
    if (eventId) params.event_id = eventId;
    api.get("/admin/analytics", { params })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [days, eventId]);

  const totals = useMemo(
    () => data?.window_totals || { total: 0, pending: 0, approved: 0, rejected: 0 },
    [data]
  );
  const approvalRate = useMemo(() => {
    const reviewed = totals.approved + totals.rejected;
    return reviewed > 0 ? Math.round((totals.approved / reviewed) * 100) : 0;
  }, [totals]);

  const selectedEvent = events.find((e) => e.id === eventId);

  return (
    <div data-testid="admin-analytics-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="term-label">/ analytics</div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-2">Reporting.</h1>
          <p className="text-white/60 mt-2 text-sm font-mono-tech">RSVP submissions, approval rates, and audience breakdown.</p>
        </div>
      </div>

      {/* FILTERS */}
      <div className="hard-border bg-cyber-surface p-4 sm:p-5 mb-6 flex flex-col sm:flex-row gap-4 sm:items-end" data-testid="analytics-filters">
        <div className="flex-1">
          <div className="term-label mb-2">/ event</div>
          <select
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            data-testid="analytics-event-select"
            className="w-full px-3 py-2.5 bg-cyber-black border border-cyber-line text-white font-mono-tech text-xs uppercase tracking-[0.1em] focus:border-cyber-green focus:outline-none"
          >
            <option value="">All events</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>{e.title}</option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <div className="term-label mb-2">/ time range</div>
          <div className="grid grid-cols-4 gap-1">
            {RANGES.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setDays(r.value)}
                data-testid={`analytics-range-${r.value}`}
                className={`px-2 py-2 font-mono-tech text-[11px] uppercase tracking-[0.1em] border transition-colors duration-150 ${
                  days === r.value
                    ? "bg-white text-cyber-black border-white"
                    : "bg-cyber-black border-cyber-line text-white hover:border-cyber-green"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* SUMMARY STATS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="analytics-summary">
        <StatCell label={<><Users size={12} weight="bold" /> / submissions</>} value={totals.total} sub={`${selectedEvent?.title || "All events"} · ${days}d`} />
        <StatCell label={<><Clock size={12} weight="bold" /> / pending review</>} value={totals.pending} accent="text-cyber-yellow" />
        <StatCell label={<><CheckCircle size={12} weight="bold" /> / confirmed</>} value={totals.approved} accent="text-cyber-green" sub={`${approvalRate}% approval rate`} />
        <StatCell label={<><XCircle size={12} weight="bold" /> / declined</>} value={totals.rejected} accent="text-cyber-red" />
      </div>

      {/* CLASS COMPOSITION (CONFIRMED ONLY) */}
      {data?.attendee_class_breakdown && (
        <div className="mt-6 hard-border bg-cyber-surface p-4 sm:p-6" data-testid="analytics-class-breakdown">
          <div className="term-label flex items-center gap-2">/ confirmed composition ({days}d)</div>
          <h2 className="font-display font-black text-2xl mt-1 mb-1">Who's in the room</h2>
          <p className="text-white/50 text-sm font-mono-tech mb-5">Approved RSVPs split by internal classification.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ClassCard
              label="Regular guests"
              value={data.attendee_class_breakdown.guest}
              total={totals.approved}
              accent="text-white"
              dim="bg-white/15"
              sub="Curated industry attendees"
            />
            <ClassCard
              label="Sponsor team"
              value={data.attendee_class_breakdown.sponsor_team}
              total={totals.approved}
              accent="text-cyber-green"
              dim="bg-cyber-green/20"
              sub="Working for an event sponsor"
            />
            <ClassCard
              label="Sponsor requests"
              value={data.attendee_class_breakdown.sponsor_request}
              total={totals.approved}
              accent="text-cyber-yellow"
              dim="bg-cyber-yellow/20"
              sub="Want to sponsor a future event"
            />
          </div>

          {data.top_sponsors?.length > 0 && (
            <div className="mt-6">
              <div className="term-label mb-3">/ top sponsor teams ({days}d)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {data.top_sponsors.map((s) => (
                  <div key={s.label} className="hard-border bg-cyber-elevated px-4 py-3 flex items-center justify-between" data-testid={`sponsor-row-${s.label}`}>
                    <span className="text-white text-sm truncate">{s.label}</span>
                    <span className="font-mono-tech text-cyber-green font-bold">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TIMESERIES CHART */}
      <div className="mt-6 hard-border bg-cyber-surface p-4 sm:p-6" data-testid="analytics-timeseries">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="term-label flex items-center gap-2"><TrendUp size={12} /> / submissions over time</div>
            <h2 className="font-display font-black text-2xl mt-1">RSVPs per day</h2>
          </div>
          <div className="font-mono-tech text-[11px] text-white/40">{loading ? "loading..." : `${data?.timeseries?.length || 0} days`}</div>
        </div>
        <div className="h-[300px] sm:h-[360px] w-full">
          {data && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.timeseries} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.total} stopOpacity={0.4} />
                    <stop offset="95%" stopColor={COLORS.total} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorApproved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.approved} stopOpacity={0.5} />
                    <stop offset="95%" stopColor={COLORS.approved} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.line} vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke="#6B7894"
                  tickFormatter={fmtDate}
                  tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  stroke="#6B7894"
                  allowDecimals={false}
                  tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.15em", paddingTop: "12px" }}
                  iconType="square"
                />
                <Area
                  type="monotone"
                  dataKey="total"
                  name="Total"
                  stroke={COLORS.total}
                  strokeWidth={2}
                  fill="url(#colorTotal)"
                />
                <Area
                  type="monotone"
                  dataKey="approved"
                  name="Confirmed"
                  stroke={COLORS.approved}
                  strokeWidth={2}
                  fill="url(#colorApproved)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* PER-EVENT BAR + ROLE/INDUSTRY PIES */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-3">
        <div className="lg:col-span-7 hard-border bg-cyber-surface p-4 sm:p-6" data-testid="analytics-per-event">
          <div className="term-label flex items-center gap-2"><ChartBar size={12} /> / per event (lifetime)</div>
          <h2 className="font-display font-black text-2xl mt-1 mb-4">RSVPs by event</h2>
          <div className="h-[280px] w-full">
            {data?.per_event?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.per_event} layout="vertical" margin={{ top: 5, right: 24, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.line} horizontal={false} />
                  <XAxis type="number" stroke="#6B7894" allowDecimals={false} tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                  <YAxis
                    type="category"
                    dataKey="event_title"
                    stroke="#6B7894"
                    tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }}
                    width={150}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.15em", paddingTop: "8px" }} iconType="square" />
                  <Bar dataKey="approved" name="Confirmed" stackId="a" fill={COLORS.approved} />
                  <Bar dataKey="pending" name="Pending" stackId="a" fill={COLORS.pending} />
                  <Bar dataKey="rejected" name="Declined" stackId="a" fill={COLORS.rejected} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="font-mono-tech text-xs text-white/40 flex items-center justify-center h-full">/ no rsvps yet</div>
            )}
          </div>
        </div>

        <div className="lg:col-span-5 hard-border bg-cyber-surface p-4 sm:p-6" data-testid="analytics-role-breakdown">
          <div className="term-label">/ roles ({days}d)</div>
          <h2 className="font-display font-black text-2xl mt-1 mb-4">Who's signing up</h2>
          <div className="h-[280px] w-full">
            {data?.role_breakdown?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.role_breakdown}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={50}
                    paddingAngle={2}
                  >
                    {data.role_breakdown.map((_, i) => (
                      <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} stroke={COLORS.surface} strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em" }} iconType="square" />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="font-mono-tech text-xs text-white/40 flex items-center justify-center h-full">/ no rsvps yet</div>
            )}
          </div>
        </div>
      </div>

      {/* INDUSTRY BREAKDOWN */}
      <div className="mt-6 hard-border bg-cyber-surface p-4 sm:p-6" data-testid="analytics-industry">
        <div className="term-label">/ industry / vertical ({days}d)</div>
        <h2 className="font-display font-black text-2xl mt-1 mb-4">Verticals represented</h2>
        <div className="h-[280px] w-full">
          {data?.industry_breakdown?.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.industry_breakdown} margin={{ top: 5, right: 16, left: -10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.line} vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke="#6B7894"
                  tick={{ fontFamily: "JetBrains Mono", fontSize: 9 }}
                  angle={-30}
                  textAnchor="end"
                  height={70}
                  interval={0}
                />
                <YAxis stroke="#6B7894" allowDecimals={false} tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="RSVPs" fill={COLORS.approved} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="font-mono-tech text-xs text-white/40 flex items-center justify-center h-full">/ no data for this window</div>
          )}
        </div>
      </div>

      {/* PER-EVENT TABLE (with approval rate + class breakdown) */}
      <div className="mt-6 hard-border bg-cyber-surface p-4 sm:p-6" data-testid="analytics-event-table">
        <div className="term-label">/ leaderboard</div>
        <h2 className="font-display font-black text-2xl mt-1 mb-1">Per-event breakdown</h2>
        <p className="text-white/50 text-sm font-mono-tech mb-5">Confirmed RSVPs split by Guest / Sponsor Team / Sponsor Request, lifetime.</p>
        {data?.per_event?.length > 0 ? (
          <div className="overflow-x-auto">
            <div className="min-w-[820px]">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-cyber-line bg-cyber-elevated font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/60">
                <div className="col-span-4">Event</div>
                <div className="col-span-1 text-right">Total</div>
                <div className="col-span-2 text-right text-cyber-green">Confirmed</div>
                <div className="col-span-1 text-right text-white/80">Guests</div>
                <div className="col-span-1 text-right text-cyber-green">Sponsor team</div>
                <div className="col-span-1 text-right text-cyber-yellow">Sponsor req.</div>
                <div className="col-span-2 text-right">Approval %</div>
              </div>
              {data.per_event.map((row) => (
                <div key={row.event_id} className="grid grid-cols-12 gap-2 px-3 py-3 border-b border-cyber-line/60 items-center text-sm" data-testid={`event-row-${row.event_slug}`}>
                  <div className="col-span-4">
                    <Link to={`/admin/events`} className="text-white hover:text-cyber-green truncate block">{row.event_title}</Link>
                    <div className="font-mono-tech text-[10px] text-white/40 mt-0.5">/{row.event_slug}</div>
                  </div>
                  <div className="col-span-1 text-right font-mono-tech text-white">{row.total}</div>
                  <div className="col-span-2 text-right font-mono-tech text-cyber-green font-bold">{row.approved}</div>
                  <div className="col-span-1 text-right font-mono-tech text-white">{row.guests}</div>
                  <div className="col-span-1 text-right font-mono-tech text-cyber-green">{row.sponsor_team}</div>
                  <div className="col-span-1 text-right font-mono-tech text-cyber-yellow">{row.sponsor_request}</div>
                  <div className="col-span-2 text-right font-mono-tech text-white">{row.approval_rate}%</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="font-mono-tech text-xs text-white/40 py-6 text-center">/ no rsvps yet</div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
